/**
 * Authentication Validators
 * Contains validation functions for auth-related data structures
 */

import type { ValidationResult } from '@/types/validators';
import type {
    ForgotPasswordRequest,
    ResetPasswordRequest,
    SignInRequest,
    SignUpRequest,
} from './api';
import { REGISTER_PASSWORD_REQUIREMENTS } from './constants';
import type { RegisterFormData } from './entities';
import type { AuthFormSignInData, AuthFormSignUpData } from './state';

/**
 * Email validation regex pattern
 */
const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

/**
 * Password requirements regex patterns
 */
const PASSWORD_PATTERNS = {
    /** At least 8 characters */
    MIN_LENGTH: /.{8,}/,
    /** Maximum 128 characters */
    MAX_LENGTH: 128,
    /** At least one uppercase letter */
    UPPERCASE: /[A-Z]/,
    /** At least one lowercase letter */
    LOWERCASE: /[a-z]/,
    /** At least one number */
    NUMBER: /\d/,
    /** At least one special character */
    SPECIAL: /[!@#$%^&*(),.?":{}|<>]/,
};

/**
 * Validates an email address
 */
const validateEmail = (email: string): string[] => {
    const errors: string[] = [];
    if (!email) {
        errors.push('Email is required');
    } else if (!EMAIL_PATTERN.test(email)) {
        errors.push('Invalid email format');
    }
    return errors;
};

/**
 * Validates a password against security requirements
 *
 * Requirements:
 * - 8-128 characters long
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */
const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (!password) {
        errors.push('Password is required');
        return errors;
    }

    if (!PASSWORD_PATTERNS.MIN_LENGTH.test(password)) {
        errors.push('Password must be at least 8 characters long');
    }
    if (password.length > PASSWORD_PATTERNS.MAX_LENGTH) {
        errors.push(`Password must be less than ${PASSWORD_PATTERNS.MAX_LENGTH} characters long`);
    }
    if (!PASSWORD_PATTERNS.UPPERCASE.test(password)) {
        errors.push('Password must contain at least one uppercase letter');
    }
    if (!PASSWORD_PATTERNS.LOWERCASE.test(password)) {
        errors.push('Password must contain at least one lowercase letter');
    }
    if (!PASSWORD_PATTERNS.NUMBER.test(password)) {
        errors.push('Password must contain at least one number');
    }
    if (!PASSWORD_PATTERNS.SPECIAL.test(password)) {
        errors.push('Password must contain at least one special character');
    }

    return errors;
};

/**
 * Validates sign-in form data
 *
 * Note: The form uses 'username' field which accepts an email address,
 * while the API expects an 'email' field. This mapping is intentional
 * to maintain backwards compatibility with existing implementations
 * while allowing future flexibility for username/email login options.
 */
export const validateSignInForm = (data: AuthFormSignInData): ValidationResult<SignInRequest> => {
    const errors: string[] = [];

    // Validate username (email address)
    if (!data.username) {
        errors.push('Email address is required');
    } else if (!EMAIL_PATTERN.test(data.username)) {
        errors.push('Please enter a valid email address');
    }

    // Validate password
    if (!data.password) {
        errors.push('Password is required');
    }

    if (errors.length > 0) {
        return { success: false, errors };
    }

    // Transform form data to API request format
    return {
        success: true,
        data: {
            email: data.username, // Map username field to email for API compatibility
            password: data.password,
        },
    };
};

/**
 * Validates sign-up form data
 */
export const validateSignUpForm = (data: AuthFormSignUpData): ValidationResult<SignUpRequest> => {
    const errors: string[] = [];

    // Validate email
    errors.push(...validateEmail(data.email));

    // Validate password
    errors.push(...validatePassword(data.password));

    // Validate password confirmation
    if (data.password !== data.confirmPassword) {
        errors.push('Passwords do not match');
    }

    if (errors.length > 0) {
        return { success: false, errors };
    }

    return {
        success: true,
        data: {
            email: data.email,
            password: data.password,
            verifyPassword: data.confirmPassword,
            verificationCode: '', // This will be set later in the flow
        },
    };
};

/**
 * Validates password reset request data
 */
export const validateResetPassword = (
    data: ResetPasswordRequest,
): ValidationResult<ResetPasswordRequest> => {
    const errors: string[] = [];

    // Validate email
    errors.push(...validateEmail(data.email));

    // Validate confirmation code
    if (!data.confirmationCode) {
        errors.push('Confirmation code is required');
    }

    // Validate new password
    errors.push(...validatePassword(data.newPassword));

    if (errors.length > 0) {
        return { success: false, errors };
    }

    return { success: true, data };
};

/**
 * Validates forgot password request data
 */
export const validateForgotPassword = (
    data: ForgotPasswordRequest,
): ValidationResult<ForgotPasswordRequest> => {
    const errors = validateEmail(data.email);

    if (errors.length > 0) {
        return { success: false, errors };
    }

    return { success: true, data };
};

// ============================================================================
// Registration Validators
// ============================================================================

/**
 * Checks if a password meets all registration requirements
 */
export const isRegisterPasswordValid = (password: string): boolean => {
    return Object.values(REGISTER_PASSWORD_REQUIREMENTS).every((req) => req.test(password));
};

/**
 * Validates the self-service registration form.
 * Returns null if valid, or an error message string if invalid.
 */
export const validateRegisterForm = (formData: RegisterFormData): string | null => {
    if (!formData.firstName.trim()) return 'First name is required';
    if (!formData.lastName.trim()) return 'Last name is required';

    const email = formData.email.trim();
    if (!email) return 'Email is required';
    if (!EMAIL_PATTERN.test(email)) return 'Please enter a valid email';

    if (!formData.company.trim()) return 'Company is required';
    if (!formData.reasonForUse.trim()) return 'Please tell us how you plan to use Studio';
    if (!formData.password) return 'Password is required';
    if (!isRegisterPasswordValid(formData.password)) return 'Password does not meet requirements';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    return null;
};
